import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { alpha } from '@mui/material/styles';

import SolarIcon from '../icons/SolarIcon';
import { NEXA_FONT } from '../theme';

/**
 * Standardized top bar for every page in the app.
 *
 * Layout: [back button] [icon] [title (+ optional subtitle/breadcrumb)]
 *
 * The icon sits between the back button and the title — the pattern first
 * introduced on the Reportería page and now applied app-wide. Icons come from
 * the flat, single-color Solar Linear set (see src/icons/SolarIcon.js).
 *
 * Variants:
 *  - variant="default"  : back + icon + Nexa title on the app background.
 *  - variant="overlay"  : white back + white title with text-shadow, for use
 *                         on top of a hero image (e.g. Issue detail).
 *  - variant="primary"  : centered title on the primary color band (e.g. Amenity
 *                         reservation). Back button is white.
 *
 * Props:
 *  - title        : string (required)
 *  - icon         : Solar icon name (string) OR a ReactNode. Rendered between
 *                   the back button and the title. Pass null/undefined to omit.
 *  - onBack       : function (required) — back button handler.
 *  - subtitle     : optional string shown under the title.
 *  - breadcrumb   : optional small uppercase label shown above the title.
 *  - variant      : 'default' | 'overlay' | 'primary' (default 'default')
 *  - right        : optional ReactNode rendered on the far right.
 *  - titleSx      : optional sx overrides for the title Typography.
 *  - iconColor    : optional color for the leading icon (defaults to primary.main
 *                   for default variant, #fff for overlay/primary).
 *  - iconSize     : optional number (default 26)
 *  - sticky       : optional boolean — when true wraps in a sticky bar with
 *                   background + bottom border (default false; pages that already
 *                   own a sticky container can opt out).
 *  - sx           : optional sx for the root Box.
 */
export default function PageHeader({
  title,
  icon,
  onBack,
  subtitle,
  breadcrumb,
  variant = 'default',
  right,
  titleSx,
  iconColor,
  iconSize = 26,
  sticky = false,
  sx,
}) {
  const isOverlay = variant === 'overlay';
  const isPrimary = variant === 'primary';

  const backColor = isOverlay || isPrimary ? '#fff' : 'text.primary';
  const backHover = isOverlay
    ? 'rgba(255,255,255,0.15)'
    : isPrimary
      ? 'rgba(255,255,255,0.12)'
      : (theme) => alpha(theme.palette.text.primary, 0.08);

  const resolvedIconColor = iconColor
    ?? (isOverlay || isPrimary ? '#fff' : 'primary.main');

  const titleColor = isOverlay ? '#fff' : isPrimary ? 'primary.contrastText' : 'text.primary';
  const titleSize = isOverlay ? 22 : isPrimary ? 18 : 24;
  const titleWeight = isPrimary ? 700 : 800;
  const titleShadow = isOverlay ? '0 2px 12px rgba(0,0,0,0.35)' : undefined;

  const renderIcon = () => {
    if (!icon) return null;
    if (typeof icon === 'string') {
      return <SolarIcon name={icon} size={iconSize} color={resolvedIconColor} />;
    }
    // Allow passing a custom node (e.g. GestionTypeIcon) for dynamic icons.
    return (
      <Box sx={{ color: resolvedIconColor, display: 'flex' }}>
        {React.cloneElement(icon, { color: 'inherit' })}
      </Box>
    );
  };

  const header = (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent={isPrimary ? 'center' : 'flex-start'}
      sx={{ width: '100%' }}
    >
      <IconButton
        onClick={onBack}
        size={isOverlay ? 'small' : undefined}
        sx={{
          width: isOverlay ? undefined : 40,
          height: isOverlay ? undefined : 40,
          ml: -1,
          color: backColor,
          '&:hover': { backgroundColor: backHover },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: isOverlay ? undefined : 28 }} />
      </IconButton>

      {isPrimary ? (
        // Centered title: icon omitted, title centered with right padding to balance back btn.
        <Typography
          sx={{
            flex: 1,
            textAlign: 'center',
            color: titleColor,
            fontWeight: titleWeight,
            fontSize: titleSize,
            fontFamily: NEXA_FONT,
            pr: '40px',
          }}
        >
          {title}
        </Typography>
      ) : (
        <>
          {renderIcon()}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {breadcrumb && (
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 900,
                  color: isOverlay ? 'rgba(255,255,255,0.85)' : 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  lineHeight: 1.2,
                }}
              >
                {breadcrumb}
              </Typography>
            )}
            <Typography
              noWrap
              sx={{
                fontSize: titleSize,
                fontWeight: titleWeight,
                color: titleColor,
                letterSpacing: '-0.02em',
                textShadow: titleShadow,
                fontFamily: NEXA_FONT,
                ...titleSx,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: isOverlay ? 'rgba(255,255,255,0.9)' : 'text.secondary',
                  textShadow: isOverlay ? '0 1px 6px rgba(0,0,0,0.4)' : undefined,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </>
      )}

      {right && !isPrimary && (
        <Box sx={{ ml: 'auto', flexShrink: 0 }}>{right}</Box>
      )}
    </Stack>
  );

  if (!sticky) {
    return <Box sx={sx}>{header}</Box>;
  }

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        bgcolor: isPrimary ? 'primary.main' : 'background.default',
        borderBottom: isPrimary ? 'none' : '1px solid',
        borderColor: 'divider',
        py: 1,
        px: 2,
        ...sx,
      }}
    >
      {header}
    </Box>
  );
}
